
import { Options, Vue } from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import api from '@/shared/api';
import session from '@/shared/session';

export type WorkspaceSubscriptionPlanSummary = {
    key: string;
    label: string;
    description: string;
    monthlyCredits: number;
    priceEur: number;
    priceLabel: string;
    renewalInterval: string;
    renewalIntervalLabel: string;
    currency: string;
    automaticRenewalDefault: boolean;
};

const PLAN_DISPLAY_ORDER = ['trial', 'paid'] as const;

@Options({})
export default class SubscriptionManage extends Vue {
    @Prop({ default: 'upgrade' })
    readonly routeMode!: 'upgrade' | 'extend';

    plansLoading = true;
    plansError = '';
    actionError = '';
    actionLoading = false;
    plans: WorkspaceSubscriptionPlanSummary[] = [];
    upgradePlanKey = 'paid';
    currentPlanKey: 'trial' | 'paid' | null = null;

    get mode(): 'upgrade' | 'extend' {
        const mode = String(this.routeMode || 'upgrade').toLowerCase();
        return mode === 'extend' ? 'extend' : 'upgrade';
    }

    get orderedPlans(): WorkspaceSubscriptionPlanSummary[] {
        const order = PLAN_DISPLAY_ORDER as readonly string[];
        return [...this.plans].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
    }

    get pageSubtitle(): string {
        if (this.mode === 'extend') {
            return 'Compare plans for your workspace. Extend your subscription to add another billing period.';
        }
        if (!this.currentPlanKey || this.currentPlanKey === 'trial') {
            return 'Compare available plans. Upgrade to continue with full monthly credits after your trial.';
        }
        return 'Compare available plans for your workspace.';
    }

    /** Plan that should show the primary action (upgrade / extend / start). */
    get upgradeTargetKey(): string | null {
        if (this.mode === 'extend') {
            if (this.currentPlanKey === 'paid') {
                return 'paid';
            }
            return null;
        }
        if (this.currentPlanKey === 'paid') {
            return null;
        }
        return this.upgradePlanKey;
    }

    get showActionButton(): boolean {
        return this.upgradeTargetKey !== null;
    }

    get actionButtonLabel(): string {
        if (this.mode === 'extend') {
            return 'Extend subscription';
        }
        if (!this.currentPlanKey) {
            return 'Start subscription';
        }
        return 'Upgrade';
    }

    isCurrentPlan(plan: WorkspaceSubscriptionPlanSummary): boolean {
        return this.currentPlanKey !== null && plan.key === this.currentPlanKey;
    }

    isUpgradeTarget(plan: WorkspaceSubscriptionPlanSummary): boolean {
        return this.upgradeTargetKey !== null && plan.key === this.upgradeTargetKey;
    }

    async created() {
        await Promise.all([this.loadPlans(), this.refreshCurrentSubscription()]);
    }

    async refreshCurrentSubscription() {
        try {
            const businessUnits = await api.getBusinessUnits();
            const currentId = session.getCurrentBusinessUnit();
            const current = businessUnits.find((bu: { id: string }) => bu.id === currentId) ?? businessUnits[0];
            const sub = current?.subscription;
            if (sub === 'trial' || sub === 'paid') {
                this.currentPlanKey = sub;
            } else {
                this.currentPlanKey = null;
            }
        } catch {
            const fallback = session.getBusinessUnits().find((bu) => bu.id === session.getCurrentBusinessUnit());
            const sub = fallback?.subscription;
            this.currentPlanKey = sub === 'trial' || sub === 'paid' ? sub : null;
        }
    }

    async loadPlans() {
        this.plansLoading = true;
        this.plansError = '';
        try {
            const response = await api.getBusinessUnitSubscriptionPlans();
            this.plans = response.plans ?? [];
            this.upgradePlanKey = response.upgradePlanKey ?? 'paid';
        } catch (e: any) {
            this.plansError = e?.message || 'Could not load subscription plans.';
        } finally {
            this.plansLoading = false;
        }
    }

    async onPlanAction(_plan: WorkspaceSubscriptionPlanSummary) {
        if (this.actionLoading) {
            return;
        }

        this.actionError = '';

        if (this.mode === 'extend') {
            this.actionError = 'Extending your subscription is not available yet.';
            return;
        }

        if (this.currentPlanKey === 'paid') {
            this.actionError = 'You are already on the paid plan.';
            return;
        }

        if (this.currentPlanKey !== 'trial' && this.currentPlanKey !== null) {
            this.actionError = 'Upgrade is only available from a trial subscription.';
            return;
        }

        this.actionLoading = true;
        try {
            const { orderId, paymentRequired } = await api.createBusinessUnitUpgradeOrder();

            if (paymentRequired) {
                const payResponse = await api.molliePay({ orderId });
                if (payResponse?.url) {
                    window.location.href = payResponse.url;
                    return;
                }
                throw new Error('Payment could not be started.');
            }

            await api.completeBusinessUnitSubscriptionPayment(orderId);
            await session.refreshBusinessUnits();
            await this.refreshCurrentSubscription();
            await this.$router.push({ name: 'credits' });
        } catch (e: unknown) {
            this.actionError = e instanceof Error ? e.message : 'Could not start upgrade.';
        } finally {
            this.actionLoading = false;
        }
    }
}
